/**
 * Anti-corruption layer for the community site map API response.
 *
 * @module features/nativeInteractiveSiteMap/services/processCommunityMap
 */

import LotStatus from '../enums/lotStatus';

function resolveLotStatus(rawStatus) {
  const normalized = rawStatus?.toLowerCase() ?? '';
  return (LotStatus.fromKey(normalized) ?? LotStatus.Unavailable).toString();
}

/**
 * Maps raw community site map lots to the internal lot shape.
 *
 * housePlanIds and housingUnitId are mutually exclusive in practice: a lot with a housing unit
 * already built on it (housingUnitId set) is never also a multi-plan buildable lot, and vice
 * versa — confirmed against the data with product. Selectors that branch on one field (e.g.
 * redux/selectors.js#selectLotRoutingOutcome) rely on this holding.
 *
 * @param {object[]} lots - Raw PascalCase lot entries
 * @returns {object[]}
 */
function mapLots(lots) {
  return (lots ?? []).map((lot) => ({
    sitePlanLotId: lot.SitePlanLotId,
    status: resolveLotStatus(lot.LotStatusType),
    housePlanIds: lot.HousePlanIds ?? [],
    housingUnitId: lot.HousingUnitId ?? null,
  }));
}

/**
 * Maps a raw API community site map to the internal SiteMapResource shape.
 *
 * @param {object} siteMapApiResponse - Raw community site map from the API response (PascalCase)
 * @returns {SiteMapResource}
*/
function mapCommunitySiteMapToResource(siteMapApiResponse) {
  return {
    mapBlobUrl:          siteMapApiResponse.SiteMapUrl,
    bottomLeftLatitude:  siteMapApiResponse.BottomLeftLatitude,
    bottomLeftLongitude: siteMapApiResponse.BottomLeftLongitude,
    topRightLatitude:    siteMapApiResponse.TopRightLatitude,
    topRightLongitude:   siteMapApiResponse.TopRightLongitude,
    lots:                mapLots(siteMapApiResponse.Lots),
  };
}

/**
 * Maps the community site map API response to the internal SiteMapResource shape.
 * Returns null for empty/falsy input so the epic's filter(Boolean) discards it.
 *
 * @param {object} siteMapApiResponse - Raw PascalCase API response
 * @returns {SiteMapResource|null}
 */
function processCommunityMap(siteMapApiResponse) {
  if (!siteMapApiResponse) {
    return null;
  }

  return mapCommunitySiteMapToResource(siteMapApiResponse);
}

export default processCommunityMap;
